import { computed, onScopeDispose, ref } from "vue";

import { companyApi } from "@/entities/company";
import type { CompanyPhoto } from "@/entities/company";
import type { AppError } from "@/shared/api";
import { i18n } from "@/shared/i18n";
import { toast } from "@/shared/lib/toast";

import type { GalleryErrorKey } from "./mapGalleryError";
import { mapGalleryError } from "./mapGalleryError";

export interface PendingUpload {
  tempId: string;
  /** object-URL локального прев'ю (revoke на done/error/dispose). */
  previewUrl: string;
  status: "uploading" | "error";
  errorKey?: GalleryErrorKey;
}

export type RemoveState = "idle" | "confirming" | "removing";

export interface UseSalonGalleryOptions {
  /** Server-authoritative колекція (живе в host). */
  getPhotos: () => CompanyPhoto[];
  /** Host-applier — змінює server-state у host. */
  setPhotos: (next: CompanyPhoto[]) => void;
  /** a11y polite announce (host aria-live region). */
  announce: (msg: string) => void;
}

function tr(key: string, named?: Record<string, unknown>): string {
  return named ? i18n.global.t(key, named) : i18n.global.t(key);
}

/** Компактує позиції: [0, 1, 2, ...] — без прогалин після видалення. */
function compact(photos: CompanyPhoto[]): CompanyPhoto[] {
  return photos.map((p, i) => ({ ...p, position: i }));
}

let tempIdSeq = 0;

/**
 * Transient composable галереї (spec §3.3, D6). Server-state живе в host;
 * composable тримає лише ефемерний UI-стан (pending-tiles, dialog, reorderInFlight).
 * Upload: pending-tile → 201 → append; Error → inline / toast.
 * Delete: confirm-dialog (AlertDialog); 204 / 404 `photo_not_found` → тихий успіх (D9).
 * Reorder: optimistic swap + revert на помилці.
 * Cleanup: revoke object-URLs + abort upload на `onScopeDispose`.
 */
export function useSalonGallery(opts: UseSalonGalleryOptions) {
  const pendingUploads = ref<PendingUpload[]>([]);
  const uploadErrorKey = ref<GalleryErrorKey | null>(null);
  const removeState = ref<RemoveState>("idle");
  const removeTargetId = ref<string | null>(null);
  const removeErrorKey = ref<GalleryErrorKey | null>(null);
  const reorderInFlight = ref(false);

  const isFull = computed(() => opts.getPhotos().length >= 10);

  const uploadControllers = new Set<AbortController>();

  function revokeAll(): void {
    for (const p of pendingUploads.value) {
      URL.revokeObjectURL(p.previewUrl);
    }
  }

  // ---- Upload machine ----

  async function uploadFile(file: File): Promise<void> {
    if (isFull.value) {
      uploadErrorKey.value = "salon.gallery.errors.full";
      return;
    }

    const tempId = `pending-${++tempIdSeq}`;
    const previewUrl = URL.createObjectURL(file);
    pendingUploads.value = [
      ...pendingUploads.value,
      { tempId, previewUrl, status: "uploading" },
    ];
    uploadErrorKey.value = null;
    const controller = new AbortController();
    uploadControllers.add(controller);

    try {
      const photo = await companyApi.uploadPhoto(file, { signal: controller.signal });

      URL.revokeObjectURL(previewUrl);
      pendingUploads.value = pendingUploads.value.filter((p) => p.tempId !== tempId);
      opts.setPhotos([...opts.getPhotos(), photo]);
      toast.success(tr("salon.gallery.uploaded"));
    } catch (e) {
      if (controller.signal.aborted) {
        URL.revokeObjectURL(previewUrl);
        pendingUploads.value = pendingUploads.value.filter((p) => p.tempId !== tempId);
        return;
      }

      const mapped = mapGalleryError(e as AppError);
      URL.revokeObjectURL(previewUrl);
      pendingUploads.value = pendingUploads.value.filter((p) => p.tempId !== tempId);

      if (mapped.surface === "inline" || mapped.surface === "reorder") {
        uploadErrorKey.value = mapped.i18nKey ?? null;
      } else {
        toast.error(tr(mapped.i18nKey ?? "errors.unknown"));
      }
    } finally {
      uploadControllers.delete(controller);
    }
  }

  // ---- Delete machine ----

  function requestRemove(id: string): void {
    removeState.value = "confirming";
    removeTargetId.value = id;
    removeErrorKey.value = null;
  }

  function cancelRemove(): void {
    removeState.value = "idle";
    removeTargetId.value = null;
    removeErrorKey.value = null;
  }

  async function confirmRemove(): Promise<void> {
    if (removeState.value === "removing" || !removeTargetId.value) return;

    const id = removeTargetId.value;
    removeState.value = "removing";
    removeErrorKey.value = null;

    try {
      await companyApi.deletePhoto(id);
      applyRemoveSuccess(id);
    } catch (e) {
      const mapped = mapGalleryError(e as AppError);

      if (mapped.surface === "silent") {
        // D9: 404 photo_not_found → тихий успіх (вже видалено на сервері)
        applyRemoveSuccess(id);
        return;
      }

      removeErrorKey.value = mapped.i18nKey ?? ("errors.unknown" as GalleryErrorKey);
      removeState.value = "confirming";
    }
  }

  function applyRemoveSuccess(id: string): void {
    const next = compact(opts.getPhotos().filter((p) => p.id !== id));
    opts.setPhotos(next);
    removeState.value = "idle";
    removeTargetId.value = null;
    toast.success(tr("salon.gallery.deleted"));
  }

  // ---- Reorder machine ----

  async function moveLeft(id: string): Promise<void> {
    const photos = opts.getPhotos();
    const idx = photos.findIndex((p) => p.id === id);
    if (idx <= 0 || reorderInFlight.value) return;
    await reorder(photos, idx, idx - 1);
  }

  async function moveRight(id: string): Promise<void> {
    const photos = opts.getPhotos();
    const idx = photos.findIndex((p) => p.id === id);
    if (idx < 0 || idx >= photos.length - 1 || reorderInFlight.value) return;
    await reorder(photos, idx, idx + 1);
  }

  async function reorder(
    photos: CompanyPhoto[],
    fromIdx: number,
    toIdx: number,
  ): Promise<void> {
    const snapshot = [...photos];

    const newOrder = [...photos];
    [newOrder[fromIdx], newOrder[toIdx]] = [newOrder[toIdx], newOrder[fromIdx]];
    const withPositions = newOrder.map((p, i) => ({ ...p, position: i }));

    // Optimistic update — host бачить зміну одразу
    opts.setPhotos(withPositions);
    opts.announce(tr("salon.gallery.a11y.moved", { pos: toIdx + 1, total: photos.length }));

    reorderInFlight.value = true;
    try {
      const result = await companyApi.reorderPhotos(
        withPositions.map((p) => ({ id: p.id, position: p.position })),
      );
      opts.setPhotos(result);
    } catch {
      // Revert до snapshot
      opts.setPhotos(snapshot);
      toast.error(tr("salon.gallery.errors.reorderFailed"));
    } finally {
      reorderInFlight.value = false;
    }
  }

  // ---- Cleanup ----

  onScopeDispose(() => {
    uploadControllers.forEach((c) => c.abort());
    revokeAll();
  });

  return {
    pendingUploads,
    uploadErrorKey,
    removeState,
    removeTargetId,
    removeErrorKey,
    reorderInFlight,
    isFull,
    uploadFile,
    requestRemove,
    cancelRemove,
    confirmRemove,
    moveLeft,
    moveRight,
  };
}

export type SalonGallery = ReturnType<typeof useSalonGallery>;
